// @ts-strict-ignore
import { Component, computed, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { ProjectSaveInfoVO } from '@project/model/vo/project.vo';
import { ProjectModel } from '@project/model/project.model';
import { ProjectOrderingUtils } from '@project/components/project/util/project-ordering-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { escape } from 'lodash';
import { EditProjectVersionResult } from '../edit-project-version/edit-project-version.component';
import { ProjectService } from '../../import-export/service/project.service';
import { ProjectComponentPresenter } from '../../project-component.presenter';
import { UserModel } from '@user/model/user.model';

export enum SaveProjectCloseType {
  SAVE_AS_NEW_PROJECT = 1,
  SAVE_AS_NEW_VERSION = 2,
  OVERWRITE_PROJECT = 3,
  SAVE_AND_SPLIT = 4,
  OVERWRITE_ANY_VERSION = 5,
}

export interface SaveProjectPopupData {
  detail: SaveProjectCloseType;
  projectSaveInfo: ProjectSaveInfoVO;
}

@Component({
  standalone: false,
  templateUrl: './save.component.html',
  selector: 'app-modal-save-project',
})
export class ModalSaveProjectComponent implements OnInit {
  public readonly closeTypes = SaveProjectCloseType;

  @Input() showSaveAsNewProject: boolean = false;

  @Input() projectSaveInfo: ProjectSaveInfoVO = null;

  @Input() isEditProjectVersionPopupAlreadyOpened: boolean = false;

  @Output() public finish = new EventEmitter<SaveProjectPopupData>();

  public isWriteProtected: boolean;
  public saveAsNewVersion: boolean;
  public overwriteProject: boolean;
  public overwriteAnyVersion: boolean;
  public saveAsXCOM: boolean;

  public checkedType = signal<number | null>(null);

  public noticeText = computed(() => {
    const type = this.checkedType();
    switch (type) {
      case SaveProjectCloseType.SAVE_AS_NEW_VERSION:
        return 'save.confirm.label.saveAsNewVersionNote';
      case SaveProjectCloseType.SAVE_AS_NEW_PROJECT:
        return 'save.confirm.label.saveAsNewProjectNote';
      case SaveProjectCloseType.OVERWRITE_PROJECT:
        return 'save.confirm.label.overwriteProjectNote';
      case SaveProjectCloseType.SAVE_AND_SPLIT:
        return 'save.confirm.label.saveAndSplitNote';
      case SaveProjectCloseType.OVERWRITE_ANY_VERSION:
        return 'save.confirm.label.overwriteVersionNote';
      default:
        return null;
    }
  });

  constructor(
    private projectModel: ProjectModel,
    private alertHelper: AlertHelper,
    private projectService: ProjectService,
    private projectComponentPresenter: ProjectComponentPresenter,
    private userModel: UserModel,
  ) {}

  public ngOnInit(): void {
    this.isWriteProtected = ProjectOrderingUtils.isWriteProtected(this.projectModel.currentProject, this.projectModel.user);
    this.saveAsNewVersion = this.showSaveAsNewVersion();
    this.overwriteProject = this.showOverwriteProject();
    this.saveAsXCOM = this.xcomSaveAvailable();
    this.overwriteAnyVersion = this.showOverwriteAnyVersion();

    this.preselectItem();
  }

  public close(detail: number): void {
    this.finish.emit({
      detail: detail,
      projectSaveInfo: this.projectSaveInfo
    });
  }

  public saveButtonClickHandler(): void {
    if (this.checkedType()) {
      if (this.checkedType() === SaveProjectCloseType.SAVE_AND_SPLIT
        && this.projectSaveInfo && ArrayUtils.isNotEmpty(this.projectSaveInfo.readOnlySubProjectNames)) {
        let text: string = '';
        this.projectSaveInfo.readOnlySubProjectNames.forEach((item) => text += '\n' + item);
        this.alertHelper.showError('save.title.cannot_override_write_protected_subproject',
          'save.message.cannot_override_write_protected_subproject', {translationParams: [text]});
        return;
      }
      this.continueSave(this.checkedType());
    }
  }

  public check(value: number): void {
    this.checkedType.set(value);
  }

  private preselectItem(): void {
    switch (true) {
      case this.saveAsNewVersion:
        this.checkedType.set(SaveProjectCloseType.SAVE_AS_NEW_VERSION);
        break;
      case this.saveAsXCOM:
        this.checkedType.set(SaveProjectCloseType.SAVE_AND_SPLIT);
        break;
      case this.showSaveAsNewProject:
        this.checkedType.set(SaveProjectCloseType.SAVE_AS_NEW_PROJECT);
        break;
      case this.overwriteProject:
        this.checkedType.set(SaveProjectCloseType.OVERWRITE_PROJECT);
        break;
    }
  }

  private continueSave(selectedCloseType: number): void {
    if (selectedCloseType === SaveProjectCloseType.SAVE_AND_SPLIT
      && this.projectSaveInfo && ArrayUtils.isNotEmpty(this.projectSaveInfo.existingSubProjectNames)) {
      let text: string = '';
      this.projectSaveInfo.existingSubProjectNames.forEach((item) => text += '\n' + item);
      this.alertHelper.showAlert('save.title.confirm_override_subproject', 'save.message.confirm_override_subproject', {0: escape(text)},
        [AlertButton.YES, AlertButton.NO], 'error').then((answer) => {
          if (answer === AlertButton.YES) {
            this.executeSave(selectedCloseType);
          }
        });
    } else {
      this.executeSave(selectedCloseType);
    }
  }

  private executeSave(selectedCloseType: number): void {
    if (!this.isEditProjectVersionPopupAlreadyOpened &&
      (selectedCloseType === SaveProjectCloseType.SAVE_AS_NEW_VERSION
      || selectedCloseType === SaveProjectCloseType.SAVE_AND_SPLIT)) {
      this.projectComponentPresenter.openEditProjectVersionComponent(this.projectSaveInfo).then((result: EditProjectVersionResult) => {
        if (result.isSuccess) {
            this.close(selectedCloseType);
        }
      });
    } else {
      this.close(selectedCloseType);
    }
  }

  private showSaveAsNewVersion(): boolean {
    if (this.projectModel.currentProject && this.userModel.user) {
      return ProjectOrderingUtils.canCreateNewVersion(this.projectModel.currentProject, this.userModel.user);
    }
    return false;
  }

  private showOverwriteProject(): boolean {
    if (
      this.userModel.checkPermissions(['OVERWRITE_NEWEST_VERSION']) &&
      this.projectSaveInfo &&
      this.projectSaveInfo.projectWithHighestVersion &&
      this.userModel.user
    ) {
      return !ProjectOrderingUtils.isWriteProtected(this.projectSaveInfo.projectWithHighestVersion, this.userModel.user);
    } else {
      return false;
    }
  }

  private showOverwriteAnyVersion(): boolean {
    return this.userModel.user.hasPermission('OVERWRITE_PROJECT_HISTORY');
  }

  private xcomSaveAvailable(): boolean {
    return this.projectService.isXcomSubprojectAvailable(this.projectModel.currentProject)
      && this.userModel.user.hasPermission('SAVE_PROJECT_WITH_SUBPROJECTS');
  }
}
